import React, { ComponentType, FC, useEffect, useState } from "react";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";

import { ConnectionInfo, METHOD_NAMES } from "utils/serverless-hub";

import { loadConnectionInfo } from "api/serverless-hub-api";

interface Props {
    webSocketPayload: string | null;

    addWebSocketUserId: (userId: string) => void;
    addWebSocketHabName: (habName: string) => void;
}

const withWebSocket = <P extends object>(
    Component: ComponentType<P & Props>
): FC<P> => {
    // eslint-disable-next-line react/display-name
    return (props: P) => {
        const [connectionInfo, setConnectionInfo] =
            useState<ConnectionInfo | null>(null);

        const [hubConnection, setHubConnection] =
            useState<HubConnection | null>(null);

        const [userId, setUserId] = useState<string | null>(null);
        const [habName, setHabName] = useState<string | null>(null);

        const [payload, setPayload] = useState<string | null>(null);

        const changeUserId = (userId: string) => {
            setUserId(userId);
        };

        const changeHabName = (habName: string) => {
            setHabName(habName);
        };

        useEffect(() => {
            if (
                userId !== null &&
                habName !== null &&
                connectionInfo === null
            ) {
                loadConnectionInfo(userId, habName)
                    .then(([payload]) => {
                        setConnectionInfo(payload);
                    })
                    .catch((error) => {
                        console.error(
                            "Failed to fetch connection info:",
                            error
                        );
                    });
            }
        }, [userId, habName, connectionInfo]);

        useEffect(() => {
            if (connectionInfo !== null) {
                const builder = new HubConnectionBuilder()
                    .withAutomaticReconnect()
                    .withUrl(connectionInfo.url, {
                        accessTokenFactory: () => connectionInfo.accessToken,
                    });

                setHubConnection(builder.build());
            }
        }, [connectionInfo]);

        useEffect(() => {
            if (habName !== null && hubConnection !== null) {
                hubConnection.on(METHOD_NAMES.DEFAULT, (payload: string) => {
                    setPayload(payload);
                });

                hubConnection
                    .start()
                    .then(() => {
                        console.log("Hub connection started successfully.");
                    })
                    .catch((error) => {
                        console.error("Failed to start hub connection:", error);
                    });
            }

            return () => {
                hubConnection?.stop().catch((error) => {
                    console.error("Failed to stop hub connection:", error);
                });
            };
        }, [habName, hubConnection]);

        return (
            <Component
                {...props}
                webSocketPayload={payload}
                addWebSocketUserId={changeUserId}
                addWebSocketHabName={changeHabName}
            />
        );
    };
};

export default withWebSocket;
export type { Props as WithWebSocketProps };
